import * as Switch from '@radix-ui/react-switch';
import { withOnLinkClickTracking } from 'auto-tracking';
import { TrackingProperties } from 'common-tracking';

import { styled, theme } from '../stitches.config';

const SwitchRoot = withOnLinkClickTracking(
  styled(Switch.Root, {
    width: '42px',
    height: '20px',
    backgroundColor: theme.colors.gray10,
    borderRadius: '9999px',
    border: 'none',
    cursor: 'pointer',
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.6,
    },

    variants: {
      customBackgroundColor: {
        true: {
          backgroundColor: theme.colors.gray9,
          '&[data-state=checked]': {
            backgroundColor: theme.colors.blue9,
          },
        },
      },
      customBlue: {
        true: {
          backgroundColor: 'rgba(214, 230, 255, 1)',
          '&[data-state=checked]': {
            backgroundColor: 'rgba(0, 98, 255, 1)',
          },
        },
        false: {
          backgroundColor: 'rgba(121, 121, 121, 0.16)',
          '&[data-state=checked]': {
            backgroundColor: 'rgba(121, 121, 121, 0.16)',
          },
        },
      },
      // NEW VARIANT: Clean, solid grey for 'Off', standard blue for 'On'
      solidGrey: {
        true: {
          backgroundColor: 'rgb(158, 158, 158)',
          '&[data-state=checked]': {
            backgroundColor: 'rgba(0, 98, 255, 1)',
          },
        },
      },
      customWidth: {
        large: {
          width: '44px',
        },
      },
      customHeight: {
        large: {
          height: '24px',
        },
      },
    },
  }),
);

const SwitchThumb = styled(Switch.Thumb, {
  display: 'block',
  borderRadius: '9999px',
  width: '15px',
  height: '15px',
  backgroundColor: theme.colors.white,
  transition: 'transform 100ms',
  willChange: 'transform',
  transform: 'translateX(-3px)',
  '&[data-state=checked]': {
    transform: 'translateX(16px)',
  },

  variants: {
    customSize: {
      large: {
        width: '18px',
        height: '18px',
        '&[data-state=checked]': {
          transform: 'translateX(18px)',
        },
      },
    },
    isDisabled: {
      true: {
        backgroundColor: 'rgba(214, 214, 214, 1)',
      },
    },
  },
});

type ToggleProps = {
  onClick: () => void;
  disabled?: boolean;
  checked?: boolean;
  customBackgroundColor?: boolean;
  customBlue?: boolean;
  solidGrey?: boolean;
  customWidth?: 'large';
  customHeight?: 'large';
  customSize?: 'large';
  checkedColor?: string;
  trackingProperties?: TrackingProperties; // Add trackingProperties here
  'data-test-id'?: string;
};
export const ToggleSwitch = (props: ToggleProps) => {
  const { customSize, disabled, checkedColor, ...rootProps } = props;
  return (
    <SwitchRoot
      {...rootProps}
      disabled={disabled}
      customBlue={!props.solidGrey && props.customBlue !== false && !disabled}
      solidGrey={props.solidGrey}
      css={{
        '&[data-state=checked]': {
          backgroundColor: checkedColor || theme.colors.blue11,
        },
      }}
    >
      <SwitchThumb customSize={customSize} isDisabled={disabled} />
    </SwitchRoot>
  );
};
